import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getOrders, Order } from '../lib/api';
import { 
  Truck, 
  Clock, 
  CheckCircle, 
  XCircle,
  CaretDown,
  CaretRight,
  ArrowSquareOut,
  EnvelopeSimple,
  Phone,
  Calendar
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';

export default function OrdersPage() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchOrders = async (): Promise<void> => {
      try {
        const { data } = await getOrders();
        setOrders(data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const toggleExpand = (orderId: string): void => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const getStatusConfig = (status: string | undefined): { label: string; icon: typeof CheckCircle; color: string; bg: string; border: string } => {
    switch (status) {
      case 'accepted':
        return { 
          label: 'Accepterad', 
          icon: CheckCircle, 
          color: 'text-emerald-600',
          bg: 'bg-emerald-50',
          border: 'border-emerald-200'
        };
      case 'rejected':
        return { 
          label: 'Avvisad', 
          icon: XCircle, 
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200'
        };
      case 'waiting_quote_approval':
        return { 
          label: 'Väntar på godkänd offert', 
          icon: Clock, 
          color: 'text-white',
          bg: 'bg-red-500',
          border: 'border-red-600'
        };
      case 'waiting_invoice':
        return { 
          label: 'Väntar på faktura', 
          icon: Clock, 
          color: 'text-amber-900',
          bg: 'bg-amber-300',
          border: 'border-amber-400'
        };
      case 'completed':
        return { 
          label: 'Slutförd', 
          icon: CheckCircle, 
          color: 'text-white',
          bg: 'bg-emerald-500',
          border: 'border-emerald-600'
        };
      default: // pending
        return { 
          label: 'Väntande', 
          icon: Clock, 
          color: 'text-amber-600',
          bg: 'bg-amber-50',
          border: 'border-amber-200'
        };
    }
  };

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-16 bg-slate-100 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="orders-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{t('orders')}</h1>
        <p className="text-sm text-slate-500 mt-1">Arbetsordrar skickade till leverantörer</p>
      </div>

      {orders.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state">
            <Truck size={48} className="empty-state-icon" />
            <p className="text-slate-500">{t('no_orders')}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => {
            const statusConfig = getStatusConfig(order.status);
            const StatusIcon = statusConfig.icon;
            const isExpanded = expandedOrders[order.id];

            return (
              <div
                key={order.id}
                className={`bg-white border rounded-lg overflow-hidden transition-all ${statusConfig.border}`}
                data-testid={`order-${order.id}`}
              >
                {/* Collapsed Header */}
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleExpand(order.id)}
                >
                  <div className="text-slate-400">
                    {isExpanded ? <CaretDown size={18} /> : <CaretRight size={18} />}
                  </div>
                  
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${statusConfig.bg}`}>
                    <StatusIcon size={16} className={statusConfig.color} weight="fill" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{order.subcontractor_name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {order.issue_id || 'Ärende'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500 truncate">
                      {order.instructions || 'Ingen instruktion'}
                    </p>
                  </div>
                  
                  <div className="hidden sm:flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(order.created_at)}
                    </span>
                    {order.due_date && (
                      <span className="text-amber-600">
                        Slutdatum: {formatDate(order.due_date)}
                      </span>
                    )}
                  </div>
                  
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                    {statusConfig.label}
                  </span>
                  
                  <a
                    href={`/orders/confirm/${order.access_token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
                    title="Öppna extern vy"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`view-order-${order.id}`}
                  >
                    <ArrowSquareOut size={16} />
                  </a>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50">
                    {/* Contact Info */}
                    <div className="flex flex-wrap gap-4 mb-4 text-sm">
                      {order.subcontractor_email && (
                        <a 
                          href={`mailto:${order.subcontractor_email}`}
                          className="flex items-center gap-1.5 text-slate-600 hover:text-blue-600"
                        >
                          <EnvelopeSimple size={14} />
                          {order.subcontractor_email}
                        </a>
                      )}
                      {order.subcontractor_phone && (
                        <a 
                          href={`tel:${order.subcontractor_phone}`}
                          className="flex items-center gap-1.5 text-slate-600 hover:text-blue-600"
                        >
                          <Phone size={14} />
                          {order.subcontractor_phone}
                        </a>
                      )}
                    </div>

                    {/* Instructions */}
                    <div className="mb-4">
                      <p className="text-xs font-medium text-slate-500 mb-2">Instruktioner:</p>
                      {order.instructions_html ? (
                        <div 
                          className="border border-slate-200 rounded-lg overflow-hidden bg-white"
                          dangerouslySetInnerHTML={{ __html: order.instructions_html }}
                        />
                      ) : (
                        <div className="p-3 bg-white border border-slate-200 rounded-lg">
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                            {order.instructions}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Response if any */}
                    {order.response && (
                      <div className={`p-3 rounded-lg ${
                        order.status === 'confirmed' ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                      }`}>
                        <p className="text-xs font-medium text-slate-500 mb-1">Svar från leverantör:</p>
                        <p className={`text-sm ${
                          order.status === 'confirmed' ? 'text-emerald-700' : 'text-red-700'
                        }`}>
                          {order.response}
                        </p>
                        {order.responded_at && (
                          <p className="text-[10px] text-slate-400 mt-1">
                            {new Date(order.responded_at).toLocaleString('sv-SE')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
